import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ProductDetail } from '../../types/product';
import { getLocalizedField } from '../../utils/localize';
import { CCSelectedOption, SelectedOption } from '../../context/CartContext';
import {
  fetchCCOptions,
  CCOptionGroup,
  isCCSingleSelect,
  toggleCCSelection,
  hasAllRequiredCC,
} from '../../api/ccOptions';
import { formatCurrency } from '../../utils/currency';
import type { SupportedLanguage } from '../../i18n';

// ─── CartItem type (mirrors CartContext) ─────────────────────────────────────

interface CartItem {
  product: ProductDetail;
  quantity: number;
  selectedSize?: string;
  sizePrice?: number;
  cartItemId?: number;
  selectedOptions?: SelectedOption[];
  ccOptions?: CCSelectedOption[];
  rowId?: string; // Stable row identity — see CartContext.CartItem.rowId
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface CCOptionsEditModalProps {
  item: CartItem;
  // newPrice is display-only (server recomputes at cart/checkout time).
  onConfirm: (ccOptions: CCSelectedOption[], newPrice: number) => void;
  onClose: () => void;
}

// ─── Colour tokens (matching subtask spec / Variation B palette) ──────────────

const CC_MODAL_BG  = '#FAF8F3';
const CC_CARD_BG   = '#F5F0E8';
const CC_ACCENT    = '#D4A017';
const CC_TEXT_PRI  = '#1C1511';
const CC_TEXT_MUT  = '#6B5E4E';
const CC_BORDER    = '#E8DDD0';

// ─── Styled components ────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.55);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalBox = styled.div`
  background-color: ${CC_MODAL_BG};
  border-radius: 12px;
  padding: 1.75rem;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.2rem;
  color: ${CC_TEXT_PRI};
  margin: 0;
  font-family: Georgia, 'Times New Roman', serif;
`;

const ProductName = styled.p`
  font-size: 1rem;
  color: ${CC_TEXT_MUT};
  margin: 0;
`;

const SizeList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const SizeRow = styled.li<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 1rem;
  background-color: ${props => props.$selected ? CC_CARD_BG : 'transparent'};
  border: 2px solid ${props => props.$selected ? CC_ACCENT : CC_BORDER};
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;

  &:hover {
    border-color: ${CC_ACCENT};
    background-color: ${CC_CARD_BG};
  }
`;

const RadioCircle = styled.span<{ $selected: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${props => props.$selected ? CC_ACCENT : CC_BORDER};
  background-color: ${props => props.$selected ? CC_ACCENT : 'transparent'};
  flex-shrink: 0;
  transition: border-color 0.15s, background-color 0.15s;
`;

const SizeName = styled.span`
  flex: 1;
  font-size: 0.95rem;
  color: ${CC_TEXT_PRI};
  font-weight: 500;
`;

const SizePrice = styled.span`
  font-size: 0.95rem;
  color: ${CC_TEXT_MUT};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 0.6rem 1.25rem;
  background-color: ${CC_BORDER};
  color: ${CC_TEXT_MUT};
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.8;
  }
`;

const ConfirmButton = styled.button`
  padding: 0.6rem 1.25rem;
  background-color: ${CC_ACCENT};
  color: ${CC_TEXT_PRI};
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const CCOptionsEditModal: React.FC<CCOptionsEditModalProps> = ({ item, onConfirm, onClose }) => {
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx pattern: i18next's language can be a
  // region-qualified tag or unset, so narrow to the two v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';

  const [groups, setGroups] = useState<CCOptionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selections, setSelections] = useState<Record<number, number[]>>(() => {
    // Start from the row's current selections; a quick-add picker (no ccOptions)
    // starts empty and preselects the first item of each required group below.
    const initial: Record<number, number[]> = {};
    (item.ccOptions ?? []).forEach(o => {
      initial[o.groupId] = [...(initial[o.groupId] ?? []), o.itemId];
    });
    return initial;
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    fetchCCOptions(item.product.product_id)
      .then(data => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setGroups(list);
        // Keep only still-available items; preselect the first item of any
        // required group that has no (valid) selection.
        setSelections(prev => {
          const next: Record<number, number[]> = {};
          list.forEach(g => {
            const valid = (prev[g.id] ?? []).filter(id => g.items.some(i => i.id === id));
            if (valid.length > 0) next[g.id] = valid;
            else if (g.required && g.items.length > 0) next[g.id] = [g.items[0].id];
          });
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setGroups([]);
          setLoadFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item.product.product_id, reloadKey]);

  const selectedDetails: CCSelectedOption[] = groups.flatMap(group =>
    (selections[group.id] ?? [])
      .map(id => group.items.find(i => i.id === id))
      .filter((i): i is CCOptionGroup['items'][number] => !!i)
      .map(i => ({
        groupId: group.id,
        groupName: group.name,
        groupName_zh: group.name_zh ?? null,
        itemId: i.id,
        name: i.name,
        name_zh: i.name_zh ?? null,
        price: i.price,
      }))
  );
  const newCcTotal = selectedDetails.reduce((sum, o) => sum + o.price, 0);
  const oldCcTotal = (item.ccOptions ?? []).reduce((sum, o) => sum + o.price, 0);
  // The row's existing price (size / legacy options included) minus its old
  // cc total, plus the new cc total. A fresh quick-add starts from base_price.
  const basePrice = (item.sizePrice ?? item.product.base_price) - oldCcTotal;
  const totalPrice = basePrice + newCcTotal;
  const canConfirm = !loading && !loadFailed && hasAllRequiredCC(groups, selections);

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(selectedDetails, totalPrice);
  };

  const handleOverlayClick = () => {
    onClose();
  };

  const handleBoxClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalBox onClick={handleBoxClick} data-testid="cc-options-edit-modal">
        <div>
          <ModalTitle>{t('ccOptionsEditModal.title')}</ModalTitle>
          <ProductName>{getLocalizedField(item.product, 'name', language) as string}</ProductName>
        </div>

        {loading && <ProductName>{t('ccOptionsEditModal.loading')}</ProductName>}
        {loadFailed && (
          <>
            <ProductName role="alert">{t('ccOptionsEditModal.loadError')}</ProductName>
            <CancelButton type="button" data-testid="cc-edit-retry" onClick={() => setReloadKey(k => k + 1)}>
              {t('ccOptionsEditModal.retry')}
            </CancelButton>
          </>
        )}

        {groups.map(group => {
          const single = isCCSingleSelect(group);
          const current = selections[group.id] ?? [];
          return (
            <div key={group.id} role={single ? 'radiogroup' : 'group'} aria-label={getLocalizedField(group, 'name', language) as string}>
              <ProductName>
                {getLocalizedField(group, 'name', language) as string}
                {group.required && ` ${t('ccOptionsEditModal.required')}`}
              </ProductName>
              <SizeList>
                {group.items.map(opt => {
                  const isSelected = current.includes(opt.id);
                  return (
                    <SizeRow
                      key={opt.id}
                      $selected={isSelected}
                      role={single ? 'radio' : 'checkbox'}
                      aria-checked={isSelected}
                      data-testid={`cc-edit-option-${opt.id}`}
                      onClick={() => setSelections(prev => toggleCCSelection(prev, group, opt.id))}
                    >
                      <RadioCircle $selected={isSelected} />
                      <SizeName>{getLocalizedField(opt, 'name', language) as string}</SizeName>
                      <SizePrice>{opt.price !== 0 ? `+${formatCurrency(opt.price)}` : ''}</SizePrice>
                    </SizeRow>
                  );
                })}
              </SizeList>
            </div>
          );
        })}

        <ProductName data-testid="cc-edit-total">{formatCurrency(totalPrice)}</ProductName>

        <ButtonRow>
          <CancelButton onClick={onClose}>{t('ccOptionsEditModal.cancelButton')}</CancelButton>
          <ConfirmButton
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="cc-options-edit-confirm-button"
          >
            {t('ccOptionsEditModal.confirmButton')}
          </ConfirmButton>
        </ButtonRow>
      </ModalBox>
    </Overlay>
  );
};

export default CCOptionsEditModal;
